import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { StorageRoom } from '../../../models/Storage/StorageRoom';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { useState } from 'react';
import axiosAPI from '../../../utils/client/axiosAPI';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import style from './storage.module.scss';

type StorageRoomListItem = {
  id: number;
  name: string;
  standsCount: number;
  productsCount: number;
};

export default function StorageRoomList() {
  const {
    data: rooms = [],
    refetch,
    isLoading
  } = useQuery(['storage-rooms'], StorageRoom.all);
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StorageRoomListItem | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  // Prepare data for table
  const data: StorageRoomListItem[] = (rooms ?? []).map((room) => ({
    id: room.id,
    name: room.name,
    standsCount: room.stands?.length ?? 0,
    productsCount: room.productsCount ?? 0
  }));

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosAPI.delete(`/storage-rooms/${deleteTarget.id}`);
      toast.success('Soba uspešno obrisana!');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju sobe.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<StorageRoomListItem> = [
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div
          className="cs-table-link"
          style={{ textAlign: 'left' }}
          onClick={() => navigate(`/admin/storage-rooms/edit/${row.id}`)}
        >
          {name}
        </div>
      )
    },
    {
      title: 'Broj polica',
      dataIndex: 'standsCount',
      key: 'standsCount',
      render: (count) => <span>{count}</span>
    },
    {
      title: 'Broj proizvoda',
      dataIndex: 'productsCount',
      key: 'productsCount',
      render: (count) => <span>{count}</span>
    },
    {
      key: 'action',
      title: 'Akcije',
      render: (_, room) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            className={style.editButton}
            to={`/admin/storage-rooms/edit/${room.id}`}
          >
            <EditIcon />
          </Link>
          <button
            className={style.deleteButton}
            onClick={() => {
              setDeleteTarget(room);
              setShowDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista skladišnih soba</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <strong>{rooms.length}</strong> skladišnih soba
        </div>
        <ButtonPrimary url="/admin/storage-rooms/create">
          Dodaj Skladišnu Sobu
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        className="cs-table-wrapper"
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete sobu "${deleteTarget.name}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
